/**
 * One day of one run, the Main board read only (spec D4), from the six blocks
 * of `@depicomnlbv/platform-ui`: a one-line page header; on the left what
 * needs attention and the trips; in the centre the map at the top, the
 * timeline and the findings under it; the selected trip on the right; and
 * one explanation at the foot (UI review of 2 October 2026).
 *
 * Everything that picks what is on screen is in the address: the run and the
 * day in the path, the selected trip as `?trip=<route_id>`. So any view here
 * can be sent to a colleague, and back and forward walk through selections.
 */
import { EmptyState, Explainer, PageHeader, Section } from "@depicomnlbv/platform-ui";
import { Component, lazy, Suspense, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import type { UseQueryResult } from "@tanstack/react-query";

import { useDay, useFindings, useRuns, type Day as DayData, type Findings } from "../api";
import { dayPeriod } from "../components/dayPeriod";
import { Failure, Pending } from "../components/Failure";
import { FindingSources, FindingsPanel } from "../components/FindingsPanel";
import { NeedsAttention } from "../components/NeedsAttention";
import { PlanSwitch } from "../components/PlanSwitch";
import { Timeline } from "../components/Timeline";
import { TripInspector } from "../components/TripInspector";
import { TripList } from "../components/TripList";
import { runName } from "../runs";
import { formatDate, formatShortDate, isCalendarDate, mondayOf, zoneName } from "../time";
import { vehicleRows } from "../trips";
import { useTitle } from "../useTitle";

/**
 * The map, in a chunk of its own. Leaflet is most of its weight (measured on
 * 28 September 2026: the one bundle went from 371 kB to 529 kB with it, past
 * Vite's 500 kB warning), and only this page draws a map, so the run list
 * does not wait for it.
 */
const DayMap = lazy(() => import("../components/DayMap").then((m) => ({ default: m.DayMap })));

/** Where the map will be while its chunk loads: the same section, so the
 *  layout does not jump and a screen reader meets "Map" in the same place. */
function MapLoading() {
  return <Section className="map-panel" loading title="Map" />;
}

/**
 * Keeps the day when the map cannot be had. A tab left open across a
 * redeploy asks for a map chunk the server no longer has, and the rejected
 * import is thrown during render; without this boundary it unmounts the
 * whole application, list, timeline and inspector with it. Reloading fetches
 * the new bundle, so that is what the sentence asks for.
 */
class MapBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    // Logged, as the other failures here are: the screen says what to do,
    // the console keeps what happened.
    console.error("The map could not be loaded", error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Section
        className="map-panel"
        error="The map could not be loaded. Reload the page to try again."
        title="Map"
      />
    );
  }
}

/** Every definition on the day, in the one closed explanation at its foot. */
function DayExplainer({ day, findings }: { day: DayData; findings: UseQueryResult<Findings> }) {
  return (
    <Explainer summary="How to read this day">
      <p>Times are in {zoneName(day.time_zone)}.</p>
      <p>
        Timeline: each bar runs from departure to back at factory; circles are stops and the
        diamond is the return.
      </p>
      <p>
        Findings are counted over this day and the findings about the whole plan. A finding about a
        farm or the whole run is not tied to one day, so it is shown on every day.
      </p>
      {findings.data ? (
        <>
          <p>The checks behind these findings:</p>
          <FindingSources sources={findings.data.sources} />
        </>
      ) : null}
    </Explainer>
  );
}

function DayPlan({
  day,
  findings,
  runId,
  date,
}: {
  day: DayData;
  findings: UseQueryResult<Findings>;
  runId: number;
  date: string;
}) {
  const [params] = useSearchParams();
  const requested = params.get("trip");
  const selected = requested !== null && /^\d+$/.test(requested) ? Number(requested) : null;

  return (
    <>
      <div className="day-grid">
        <div className="day-grid__side">
          <NeedsAttention day={day} query={findings} />
          <TripList day={day} selected={selected} />
        </div>
        <div className="day-grid__centre">
          <MapBoundary>
            <Suspense fallback={<MapLoading />}>
              <DayMap day={day} selected={selected} />
            </Suspense>
          </MapBoundary>
          <Timeline day={day} selected={selected} />
          <FindingsPanel date={date} day={day} query={findings} runId={runId} />
        </div>
        <div className="day-grid__inspector">
          <TripInspector day={day} selected={requested} />
        </div>
      </div>
      <DayExplainer day={day} findings={findings} />
    </>
  );
}

/** "3 trips on 2 vehicles", or nothing on a day without trips. */
function tripsLine(day: DayData): string | null {
  const trips = day.trips.length;
  if (trips === 0) return null;
  const vehicles = vehicleRows(day.trips).length;
  return `${trips === 1 ? "1 trip" : `${trips} trips`} on ${vehicles === 1 ? "1 vehicle" : `${vehicles} vehicles`}`;
}

export function Day() {
  const params = useParams();
  const valid = /^\d+$/.test(params.runId ?? "") && isCalendarDate(params.date);
  const runId = valid ? Number(params.runId) : 0;
  const date = valid ? (params.date as string) : "";

  useTitle(valid ? `${formatShortDate(date)}, run ${runId}` : "Not a day");
  const runs = useRuns();
  const day = useDay(runId, date, valid);
  // Asked for alongside the day rather than after it: the two answers do not
  // depend on each other, and the findings are the slower of the two (the API
  // replays the operating rules over the whole horizon on every request).
  const findings = useFindings(runId, date, date, valid);

  if (!valid) {
    return (
      <>
        <PageHeader actions={<Link to="/runs">All runs</Link>} title="Not a day" />
        <EmptyState tone="critical">
          This address does not name a day of a run. Open a run from the list, then its day.
        </EmptyState>
      </>
    );
  }

  // The horizon comes from the run list, which the day's own answer does not
  // carry. Until it is read, or if it cannot be, the page goes without day
  // navigation rather than guessing a horizon.
  const run = runs.data?.runs.find((r) => r.run_id === runId);
  const inHorizon = run !== undefined && date >= run.horizon_start && date <= run.horizon_end;
  const qualifier = [
    run ? runName(run) : null,
    day.data ? tripsLine(day.data) : null,
    day.data ? zoneName(day.data.time_zone) : null,
  ].filter((part) => part !== null);

  return (
    <>
      <PageHeader
        actions={
          <>
            <PlanSwitch
              current="day"
              dayTo={`/runs/${runId}/days/${date}`}
              weekTo={`/runs/${runId}/weeks/${mondayOf(date)}`}
            />
            <Link to="/runs">All runs</Link>
          </>
        }
        period={run && inHorizon ? dayPeriod(run, date) : undefined}
        qualifier={qualifier.length > 0 ? qualifier.join(" · ") : undefined}
        title={formatDate(date)}
      />
      {day.isPending ? (
        <Pending>Reading the day</Pending>
      ) : day.isError ? (
        <Failure context={{ runId, date }} error={day.error} />
      ) : (
        <DayPlan date={date} day={day.data} findings={findings} runId={runId} />
      )}
    </>
  );
}
